import { useState, useEffect } from 'react';
import { Campaign } from '../types/campaign';
import { ZohoClient } from '../services/zoho/ZohoClient';
import { ZohoSync } from '../services/zoho/ZohoSync';

export function useZohoCampaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const client = ZohoClient.getInstance();
  const sync = new ZohoSync();

  useEffect(() => {
    fetchCampaigns();
    sync.startSync(setCampaigns);

    return () => {
      sync.stopSync();
    };
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const data = await client.listCampaigns();
      setCampaigns(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async (campaign: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setLoading(true);
      const newCampaign = await client.createCampaign(campaign);
      setCampaigns(prev => [...prev, newCampaign]);
      return newCampaign;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCampaign = async (campaignId: string, updates: Partial<Campaign>) => {
    try {
      setLoading(true);
      const updatedCampaign = await client.updateCampaign(campaignId, updates);
      setCampaigns(prev => prev.map(c => c.id === campaignId ? updatedCampaign : c));
      return updatedCampaign;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update campaign');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteCampaign = async (campaignId: string) => {
    try {
      setLoading(true);
      await client.deleteCampaign(campaignId);
      setCampaigns(prev => prev.filter(c => c.id !== campaignId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete campaign');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    campaigns,
    loading,
    error,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    refresh: fetchCampaigns
  };
}